<template>
    <div class="product-page octopus-page">

        <section class="product-hero">
            <div class="product-content">
                <div class="hero-grid">

                    <div class="hero-text">
                        <h1>Octopus™</h1>
                        <h2>Explore the Underwater World Without Limits</h2>

                        <p>
                            The Octopus™ Pan-Tilt-Zoom Underwater Observatory is Fathom Ocean's
                            most advanced underwater monitoring platform, designed for organizations
                            that require exceptional visibility, control, and long-term performance
                            beneath the surface.
                        </p>

                        <p>
                            Unlike fixed-view camera systems, Octopus allows users to remotely explore
                            underwater environments in real time through full pan, tilt, and zoom control.
                        </p>

                        <b-button @click="$router.push('/contact')">
                            Schedule a Consultation
                        </b-button>
                    </div>

                    <div class="hero-image">
                        <img
                            src="@/assets/images/octopus-product.png"
                            alt="Octopus underwater observatory with CleanSweep self-cleaning arm"
                        >
                    </div>

                </div>
            </div>
        </section>

        <section class="product-section product-benefits">
            <div class="product-content">
                <div class="pink-line"></div>
                <h2>Key Benefits</h2>

                <div class="benefit-grid">

                    <div class="benefit-card">
                        <h3>Explore Beyond a Fixed View</h3>
                        <p>
                            Remotely pan, tilt, and zoom to investigate marine habitats, inspect
                            structures, follow marine life, and observe changing underwater conditions
                            in real time.
                        </p>
                    </div>

                    <div class="benefit-card">
                        <h3>Built for Continuous Operation</h3>
                        <p>
                            Patented CleanSweep™ self-cleaning technology removes biofouling and debris,
                            maintaining clear visibility and reducing maintenance requirements during
                            long-term deployments.
                        </p>
                    </div>

                    <div class="benefit-card">
                        <h3>Create a Global Audience</h3>
                        <p>
                            Stream live underwater video directly to websites, visitor centers,
                            classrooms, YouTube, and other digital platforms.
                        </p>
                    </div>

                    <div class="benefit-card">
                        <h3>Support Research & Monitoring</h3>
                        <p>
                            Capture high-definition imagery and video while supporting long-term
                            environmental monitoring, habitat assessments, species observation, and
                            scientific research.
                        </p>
                    </div>

                    <div class="benefit-card">
                        <h3>Integrate Additional Technologies</h3>
                        <p>
                            Expand capabilities through hydrophones, underwater lighting,
                            environmental sensors, and other integrated monitoring tools.
                        </p>
                    </div>

                    <div class="benefit-card">
                        <h3>Engineered for Marine Environments</h3>
                        <p>
                            Designed specifically for permanent and semi-permanent deployment in
                            challenging underwater conditions, with flexible mounting options and
                            remote operation capabilities.
                        </p>
                    </div>

                </div>
            </div>
        </section>

        <section class="product-section product-specifications" aria-labelledby="octopus-specifications-title">
            <div class="product-content product-specifications__layout">
                <div class="product-specifications__heading">
                    <h2 id="octopus-specifications-title">Technical Specifications</h2>
                    <p>Flagship Pan-Tilt-Zoom Vision System</p>
                </div>

                <div class="technical-ledger">
                    <div><strong>Camera Type</strong><span>Pan-tilt-zoom underwater camera</span></div>
                    <div><strong>Video</strong><span>1080p HD, 30 FPS</span></div>
                    <div><strong>Controls</strong><span>360° pan, tilt, optical zoom, focus, autofocus</span></div>
                    <div><strong>Streaming</strong><span>RTSP, RTMP, RTMPS, HLS</span></div>
                    <div><strong>Compression</strong><span>H.264 and MJPEG</span></div>
                    <div><strong>Audio</strong><span>Optional hydrophone</span></div>
                    <div><strong>Self-Cleaning</strong><span>CleanSweep™ mechanical wiper</span></div>
                    <div><strong>Power</strong><span>90–250 VAC or 12–48 VDC</span></div>
                    <div><strong>Power Consumption</strong><span>15W max</span></div>
                    <div><strong>Housing</strong><span>Acetal and borosilicate glass</span></div>
                    <div><strong>Depth Rating</strong><span>50 meters</span></div>
                    <div><strong>Cable Length</strong><span>&lt;70m Ethernet, &gt;2,000m with BelugaLink</span></div>
                    <div><strong>Integration</strong><span>Sensors, illuminators, TCP/IP, and serial devices</span></div>
                </div>
            </div>
        </section>

        <section class="product-section product-applications">
            <div class="product-content">
                <div class="pink-line"></div>
                <h2>Applications</h2>

                <div class="benefit-grid">

                    <div class="benefit-card">
                        <h3>Research & Conservation</h3>
                        <p>
                            Monitor marine ecosystems, study species behavior, document environmental
                            change, and support conservation initiatives through continuous underwater
                            observation.
                        </p>
                    </div>

                    <div class="benefit-card">
                        <h3>Infrastructure & Utilities</h3>
                        <p>
                            Inspect intake structures, seawalls, docks, pilings, and critical underwater
                            assets without costly or frequent dive operations.
                        </p>
                    </div>

                    <div class="benefit-card">
                        <h3>Aquariums & Public Exhibits</h3>
                        <p>
                            Create interactive underwater experiences that engage visitors while
                            providing educational and scientific value.
                        </p>
                    </div>

                    <div class="benefit-card">
                        <h3>Tourism & Public Engagement</h3>
                        <p>
                            Bring marine environments to life through immersive live streams that
                            inspire curiosity, stewardship, and community engagement.
                        </p>
                    </div>

                </div>
            </div>
        </section>

        <section class="product-section operational-profile">
            <div class="product-content operational-profile__layout">
                <h2>Ideal For</h2>

                <div class="ideal-grid">
                    <div class="card">Research Institutions</div>
                    <div class="card">Universities & Marine Laboratories</div>
                    <div class="card">Utilities & Infrastructure Operators</div>
                    <div class="card">Ports & Harbors</div>
                    <div class="card">Aquariums & Public Exhibits</div>
                    <div class="card">Government Agencies</div>
                    <div class="card">Conservation Organizations</div>
                    <div class="card">Tourism & Public Engagement Projects</div>
                </div>
            </div>
        </section>

        <section class="product-section why-section product-consult">
            <div class="product-content center-content">
                <div class="pink-line"></div>
                <h2>Why Choose Octopus™?</h2>

                <p class="large-copy">
                    When your project requires more than observation — when it requires exploration,
                    investigation, and interaction — Octopus provides the visibility and control needed
                    to better understand the underwater world.
                </p>

                <div class="button-row">

	<b-button @click="$router.push('/contact')">
		Schedule a Consultation
	</b-button>

	<div class="deployments-dropdown">

		<b-button aria-haspopup="true">
			View Live Octopus Deployments
		</b-button>

		<div class="deployments-menu">

			<router-link to="/live-cameras/coral-city">
				Coral City Camera
			</router-link>

			<router-link to="/live-cameras/wahoo-bay">
				Wahoo Bay
			</router-link>

			<router-link to="/live-cameras/okaloosa-island-pier">
				Okaloosa Island Pier
			</router-link>

			<router-link to="/live-cameras/mega-lab-hawaii-1">
				MEGA Lab Hawaii
			</router-link>

		</div>

	</div>

</div>
            </div>
        </section>

    </div>
</template>

<script>
export default {
    name: 'Octopus'
}
</script>
